import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, ArrowLeft } from "lucide-react";
import api, { fmtErr } from "../lib/api";

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password-whatsapp", { identifier });
      setSent(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <img src="/logo.png" alt="ShipAny" className="h-10 w-auto" />
        {sent ? (
          <div className="mt-6" data-testid="forgot-success">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50">
              <Mail className="h-6 w-6 text-emerald-600" />
            </span>
            <h1 className="font-display mt-4 text-xl font-bold text-shipany-navy">Check your email</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              If this account exists, we've sent a <b>temporary password</b> to the registered email address.
              Sign in with it — you'll be asked to set your own new password right after.
            </p>
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">The temporary password expires in 30 minutes.</p>
            <Link to="/login" data-testid="forgot-back-login" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-shipany-blue hover:underline">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <div className="mt-6">
            <h1 className="font-display text-xl font-bold text-shipany-navy">Forgot your password?</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              Enter your registered email or phone number — we'll email a temporary password to the account's email address.
              After signing in with it, you'll set your own new password.
            </p>
            <form onSubmit={submit} className="mt-5 space-y-3.5">
              <input
                data-testid="forgot-identifier-input"
                type="text"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Email address or phone number"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-shipany-blue focus:outline-none"
              />
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600" data-testid="forgot-error">{error}</p>}
              <button data-testid="forgot-submit-btn" disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white transition-colors hover:bg-[#0648B3] disabled:opacity-60 glow-blue">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Email me a temporary password
              </button>
            </form>
            <p className="mt-5 text-center text-sm text-slate-500">
              Remembered it? <Link to="/login" data-testid="forgot-goto-login" className="font-semibold text-shipany-blue hover:underline">Sign in</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
